/**
 * The Case Context Bar's Payer field: an inline payer name that reveals a hover popover carrying the
 * payer name, its classification badge, the plan, and a rules summary. `CaseContextBar` owns the
 * `LABEL | value` pill chrome; this renders the value half and the popover.
 *
 * Absent values follow the sourced/sourceless split documented on `PayerInfoData`.
 *
 * `Popover` is used without `isLazy`, so its rows are always mounted and only visibility toggles
 * with hover/focus; tests can assert content without simulating hover.
 */
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { PayerInfoData } from '../../interface/payerInfo';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  INK_SOFT,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW
} from '../../styles/tokens';

/** Matches `PatientInfo`'s placeholder so the two popovers read as one system. */
const PLACEHOLDER = 'N/A';

/** White per the design, unlike `PatientInfo`'s `SURFACE_WARM`. */
const POPOVER_BG = '#ffffff';

const BADGE_PLUM_BG = 'var(--color-chips-plum-50, #efedf8)';
const BADGE_PLUM_FG = 'var(--color-chips-plum-500, #875882)';

/** Links the trigger to the popover text. Chakra only wires `aria-controls`, which no screen
 *  reader follows, so without this the plan, badge and rules summary are visual-only. It sits on an
 *  inner Box rather than on `PopoverContent`, whose `id` Chakra owns and overwrites. */
const POPOVER_ID = 'payer-info-popover-content';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

/**
 * Whether a bound field carries genuinely displayable content — stricter than `PatientInfo`'s
 * `na()`, which lets `''` and `'   '` through and renders them blank.
 *
 * `Number.isFinite` rather than `typeof v === 'number'`: `NaN`/`Infinity` are numbers, and a typeof
 * check would paint their literal text as though it were real payer data.
 */
const present = (v: unknown): v is string | number =>
  Number.isFinite(v) || (typeof v === 'string' && v.trim() !== '');

/** The bound value verbatim when present, else the placeholder. Present strings are NOT trimmed. */
const na = (v: unknown): string => (present(v) ? String(v) : PLACEHOLDER);

/** The payer-type badge, right-aligned on the payer-name row. Only rendered for a bound
 *  classification, so it has no unknown state. */
const PayerTypeBadge: React.FC<{ readonly payerType: string | number }> = ({
  payerType
}) => (
  <Flex
    align="center"
    gap="5px"
    flexShrink={0}
    bg={BADGE_PLUM_BG}
    color={BADGE_PLUM_FG}
    borderRadius="9999px"
    padding="2px 9px"
    data-testid="payer-info-type"
    data-payer-type-known="true"
  >
    <Box
      width="5px"
      height="5px"
      borderRadius="9999px"
      bg={BADGE_PLUM_FG}
      flexShrink={0}
      aria-hidden
    />
    <Text as="span" textStyle="Overline 1" textTransform="uppercase">
      {String(payerType)}
    </Text>
  </Flex>
);

export const PayerInfo: React.FC<SlotProps> = ({ value }) => {
  // `value` is `unknown` — anything that is not a genuine record collapses to `{}` so every field
  // below falls back to the placeholder instead of reaching JSX as a non-primitive child.
  const data: PayerInfoData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as PayerInfoData)
      : {};

  const payerName = na(data.payerName);

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Text
          as="span"
          tabIndex={0}
          // NOT `role="button"`: Chakra's hover popover attaches focus/hover handlers but no
          // `onClick`, and a <span> gets no synthesized click from Enter/Space — so announcing a
          // button would promise an activation that does nothing. The trigger's accessible name is
          // its own payer text, and the popover is linked for announcement by `aria-describedby`
          // (Chakra wires only `aria-controls`, which screen readers do not follow).
          aria-describedby={POPOVER_ID}
          data-testid="payer-info"
          textStyle="Body 2"
          color={INK}
          // Truncation lives here, not on the host `Tag`: the payer name is the pill's only
          // unbounded value. `display` is explicit because `max-width`/`overflow`/`text-overflow`
          // do not apply to a non-replaced inline box, and `minWidth={0}` is what lets
          // `textOverflow` take effect inside the host's flex row.
          display="inline-block"
          maxWidth="14rem"
          minWidth={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          sx={HOVER_HINT_SX}
        >
          {payerName}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="payer-info-popover"
          bg={POPOVER_BG}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="14px 16px"
          width="320px"
          maxWidth="320px"
        >
          <PopoverArrow bg={POPOVER_BG} />

          <Box id={POPOVER_ID}>
            <Flex align="center" justifyContent="space-between" gap="12px">
              <Text
                as="span"
                textStyle="Body 1"
                color={INK}
                data-testid="payer-info-name"
                minWidth={0}
                // The popover WRAPS where the trigger truncates — it exists to reveal the name the
                // pill had to cut off, so ellipsising it here shows the user nothing new.
                // `overflowWrap` handles ordinary long words; `wordBreak` stops an unbroken run
                // from widening the fixed-width card.
                overflowWrap="anywhere"
                wordBreak="break-word"
              >
                {payerName}
              </Text>
              {present(data.payerType) ? (
                <PayerTypeBadge payerType={data.payerType} />
              ) : null}
            </Flex>

            <Text
              as="p"
              textStyle="Body 2"
              color={INK_MUTED}
              marginTop="2px"
              overflowWrap="anywhere"
              wordBreak="break-word"
              data-testid="payer-info-plan"
            >
              {na(data.payerPlan)}
            </Text>

            {/* The divider is hidden along with the section it introduces — a rule above nothing
              reads as a truncated card. */}
            {present(data.rulesSummary) ? (
              <Box
                borderTop={`1px solid ${POPOVER_BORDER}`}
                marginTop="12px"
                paddingTop="12px"
                data-testid="payer-info-divider"
              >
                <Box
                  textStyle="Overline 1"
                  color={INK_MUTED}
                  textTransform="uppercase"
                  marginBottom="6px"
                >
                  Payer rules summary
                </Box>
                <Text
                  as="p"
                  textStyle="Body 2"
                  color={INK_SOFT}
                  lineHeight="1.5"
                  // Both rules: `overflowWrap` handles ordinary long words, `wordBreak` keeps an
                  // unbroken run from widening the fixed-width card.
                  overflowWrap="anywhere"
                  wordBreak="break-word"
                  data-testid="payer-info-rules"
                >
                  {String(data.rulesSummary)}
                </Text>
              </Box>
            ) : null}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
